// @flow

import * as React from 'react';
import { connect } from 'react-redux';
import { BrandedHeader } from '@ta/modal.headers';
import { localize } from '@ta/common.i18n';
import type { ViewportCategory } from '@ta/common.state';
import { ReadMore } from '@ta/common.text';
import { Icon } from '@ta/styleguide.icon';
import { Linkify } from '@ta/social.links';
import { MemberEventOnObjectBlock } from '@ta/social.member-event';
import { InteractionLogger } from '@ta/tracking.interactions';
import { RoutedModal } from '@ta/overlays.modal';
import { HeadProperties, RouteConsumer } from '@ta/common.routing';

import {
  CardSection,
  FeedSectionPhotoCarousel,
  getOpenGraphInfoFromPhoto,
  SectionModal,
  SectionModalHeader,
  type Sponsorship,
} from '@ta/social.section-core';
import styles from './PhotoSection.less';

// The three possible layouts, full layout means include everything
// In a repost section, dont include certain bells and whistles, but do include the modal
// In a modal preview, dont include the bells and whistels and dont include the modal.
type PhotoSectionLayout = 'FULL' | 'REPOST_NO_MODAL' | 'REPOST_WITH_MODAL';

export type PhotoSectionProps = {|
  member: any,
  actor: any,
  actorForUrl?: any,
  photo: any,
  deleteObject?: ?() => void,
  untagObject?: ?() => void,
  loggedInUserId?: string,
  isSingleObject?: boolean,
  layout: PhotoSectionLayout,
  clusterId?: string,
  sponsorship?: Sponsorship,
  socialStatisticsBar?: React.Node,
  socialActionBar?: React.Node,
|};

type Props = {|
  ...PhotoSectionProps,

  /** Type of device the user is viewing the listings on */
  viewportCategory: ViewportCategory,
|};

const PhotoSection = ({
  photo,
  isSingleObject,
  layout,
  member,
  actor,
  actorForUrl,
  deleteObject,
  untagObject,
  loggedInUserId,
  viewportCategory,
  clusterId,
  sponsorship,
  socialActionBar,
  socialStatisticsBar,
}: Props) => {
  const isEmbedded = layout === 'REPOST_WITH_MODAL' || layout === 'REPOST_NO_MODAL';

  const header = (
    <MemberEventOnObjectBlock
      objectEventType="onPhoto"
      object={photo.object}
      member={member}
      actor={actor}
      deleteObject={deleteObject}
      untagObject={untagObject}
      loggedInUserId={loggedInUserId}
      isEmbedded={isEmbedded}
    />
  );

  const MAX_CAPTION_LENGTH = 160;

  const cta = () => (
    <div className={styles.ctaLine}>
      <span className={styles.cta}>{localize('common_Read_more') /* Read more */}</span>
      <Icon name="caret-down" />
    </div>
  );

  const metadata = {
    ...photo?.object,
    numPhotos: 1,
  };

  const photos = photo?.object ? [photo.object] : [];

  const content = [
    <CardSection.Content fullWidth key="photo">
      <InteractionLogger>
        {trackFeedClick => (
          <div className={styles.photo_wrap}>
            <div className={styles.photo}>
              <FeedSectionPhotoCarousel
                actor={actor}
                actorForUrl={actorForUrl}
                currentlyDisplayedIndex={0}
                includeRoutedModal={!isEmbedded}
                member={member}
                metadata={metadata}
                onCarouselClick={e => {
                  trackFeedClick(e, 'feedClick');
                }}
                overflowMenuObject={metadata}
                photos={photos}
                socialActionBar={socialActionBar}
                socialStatisticsBar={socialStatisticsBar}
              />
            </div>
          </div>
        )}
      </InteractionLogger>
      {(layout === 'REPOST_WITH_MODAL' || layout === 'FULL') && (
        <React.Fragment>
          <RouteConsumer>
            {route =>
              route.route && route.route.modal === `Photo_${photo.object.id}` ? (
                <HeadProperties openGraphInfo={getOpenGraphInfoFromPhoto(photo.object)} />
              ) : null
            }
          </RouteConsumer>
          <RoutedModal
            path={{ modal: `Photo_${photo.object.id}` }}
            header={
              <SectionModalHeader>
                <BrandedHeader branding="TA_GREEN" />
              </SectionModalHeader>
            }
            closeXColorVariant="WHITE"
            isFullBleed
            bodyScroll={false}
            fullscreenAlways
          >
            <SectionModal
              images={[photo.object]}
              actor={actor}
              deleteObject={deleteObject}
              untagObject={untagObject}
              showPOISavesIcon
              socialStatisticsBar={socialStatisticsBar}
              socialActionBar={socialActionBar}
              overflowMenuObject={photo.object}
            />
          </RoutedModal>
        </React.Fragment>
      )}
    </CardSection.Content>,
    <CardSection.Content key={`caption_${photo.object.id}`}>
      {photo.object.caption && (
        <InteractionLogger>
          {logInteraction => (
            <div className={styles.caption}>
              {viewportCategory === 'MOBILE' && photo.object.caption.length > MAX_CAPTION_LENGTH ? (
                <ReadMore numLinesToShow={3} cta={cta()} ctaNewline>
                  <Linkify
                    text={photo.object.caption}
                    socialReferences={photo.object.socialReferences}
                    useSpan
                    onLinkReferenceClick={(e, url) => {
                      logInteraction(e, 'linkReferenceClick', {
                        authorUserId: actor.userId,
                        url,
                      });
                    }}
                  />
                </ReadMore>
              ) : (
                <Linkify
                  text={photo.object.caption}
                  socialReferences={photo.object.socialReferences}
                  useSpan
                  onLinkReferenceClick={(e, url) => {
                    logInteraction(e, 'linkReferenceClick', {
                      authorUserId: actor.userId,
                      url,
                    });
                  }}
                />
              )}
            </div>
          )}
        </InteractionLogger>
      )}
    </CardSection.Content>,
  ];

  return (
    <CardSection
      isSingleObject={isSingleObject}
      header={header}
      content={content}
      socialStatisticsBar={socialStatisticsBar}
      socialActionBar={socialActionBar}
      pois={photo.object.location ? [photo.object.location] : []}
      clusterId={clusterId}
      sponsorship={sponsorship}
    />
  );
};

PhotoSection.defaultProps = {
  isSingleObject: false,
  member: null,
  actor: null,
  actorForUrl: undefined,
  deleteObject: null,
  untagObject: null,
  loggedInUserId: '',
  clusterId: undefined,
  sponsorship: undefined,
};

export default connect<Props, PhotoSectionProps, _, _, _, _>((state, props: PhotoSectionProps) => ({
  viewportCategory: state.meta.device.viewportCategory,
  ...props,
}))(PhotoSection);
